import { useEffect } from 'react'
import { useThemeStore } from '@/store/themeStore'
import { readChromeColors, readSystemScheme } from '@/lib/theme'

/**
 * Держит тему в согласии с платформой и красит хром вокруг приложения.
 * Вызывается ОДИН раз в корне приложения.
 *
 * Три слушателя, потому что источников системной схемы три и они не
 * заменяют друг друга:
 *  - `prefers-color-scheme` — веб-браузер;
 *  - `Telegram.WebApp.colorScheme` — начальное значение внутри Telegram;
 *  - событие `themeChanged` — пользователь переключил тему, не выходя из мини-аппа.
 */
export function useThemeSync(): void {
  const resolved = useThemeStore((state) => state.resolved)
  const setSystemScheme = useThemeStore((state) => state.setSystemScheme)

  // Источник 1 — браузер.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return

    const query = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = () => setSystemScheme(readSystemScheme())

    // Safari < 14 не знает addEventListener на MediaQueryList.
    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', handleChange)
      return () => query.removeEventListener('change', handleChange)
    }
    query.addListener(handleChange)
    return () => query.removeListener(handleChange)
  }, [setSystemScheme])

  // Источники 2 и 3 — Telegram. Мини-апп грузится асинхронно, поэтому подписка
  // ретраится, пока WebApp не появится в window.
  useEffect(() => {
    if (typeof window === 'undefined') return

    let detach: (() => void) | null = null
    let pollTimer: number | null = null

    const attach = (): boolean => {
      const webApp = window.Telegram?.WebApp as
        | (typeof window.Telegram.WebApp & {
            onEvent?: (event: string, cb: () => void) => void
            offEvent?: (event: string, cb: () => void) => void
          })
        | undefined
      if (!webApp || typeof webApp.onEvent !== 'function') return false

      const handleThemeChanged = () => setSystemScheme(readSystemScheme())
      webApp.onEvent('themeChanged', handleThemeChanged)
      // Начальное значение: WebApp мог появиться уже с готовой colorScheme.
      handleThemeChanged()

      detach = () => {
        try {
          webApp.offEvent?.('themeChanged', handleThemeChanged)
        } catch {
          // offEvent отсутствует в старых клиентах — отписка не критична.
        }
      }
      return true
    }

    if (!attach()) {
      const startedAt = Date.now()
      pollTimer = window.setInterval(() => {
        if (attach() || Date.now() - startedAt > 10000) {
          if (pollTimer !== null) {
            window.clearInterval(pollTimer)
            pollTimer = null
          }
        }
      }, 200)
    }

    return () => {
      if (pollTimer !== null) window.clearInterval(pollTimer)
      detach?.()
    }
  }, [setSystemScheme])

  // Хром вокруг приложения: `<meta name="theme-color">` был статичным `#FFC107`,
  // а шапка/фон Telegram не перекрашивались вовсе.
  useEffect(() => {
    if (typeof document === 'undefined') return

    // Атрибут уже применён стором; цвета читаются ПОСЛЕ этого кадра, иначе
    // getComputedStyle вернёт палитру прошлой темы.
    const frame = window.requestAnimationFrame(() => {
      const { background, header } = readChromeColors()

      let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
      if (!meta) {
        meta = document.createElement('meta')
        meta.name = 'theme-color'
        document.head.appendChild(meta)
      }
      meta.content = header

      const webApp = window.Telegram?.WebApp
      try {
        webApp?.setHeaderColor?.(header)
      } catch {
        // Метод доступен с Bot API 6.1; на старых клиентах молча пропускаем.
      }
      try {
        webApp?.setBackgroundColor?.(background)
      } catch {
        // см. выше
      }
    })

    return () => window.cancelAnimationFrame(frame)
  }, [resolved])
}
